import React, { FC, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns'
import { $TSFixMe } from 'types/ts-migrate'
import DateRangePicker from 'views/components/molecules/DateRangePicker/DateRangePicker'
import DimmerLoader from 'views/components/molecules/DimmerLoader/DimmerLoader'
import Modal from 'views/components/molecules/Modal/Modal'
import { apiStateSelectors } from 'modules/api/apiState'
import { reportingApiEvents } from 'modules/api/reporting'

type Props = {
  isOpen: boolean
  onClose(): void
}

const dateFormat = 'yyyy-MM-dd'

/**
 * CSV download of the TPN report. Defaults to the same window the send
 * uploads: the current calendar month plus the two before it.
 */
const DownloadTpnReportModal: FC<Props> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch()
  const [selectedStartDate, setSelectedStartDate] = useState(format(startOfMonth(subMonths(Date.now(), 2)), dateFormat))
  const [selectedEndDate, setSelectedEndDate] = useState(format(endOfMonth(Date.now()), dateFormat))

  const isLoading = useSelector(state => apiStateSelectors.isLoading(state)([reportingApiEvents.tpnReport_request]))
  // The whole `errors` entry for the request. apiState never clears it, so a
  // new failure is detected by identity change rather than presence.
  const errors = useSelector(state =>
    apiStateSelectors.getErrorsByEvent(state)({ type: reportingApiEvents.tpnReport_request.type }),
  )
  const wasLoading = useRef(false)
  const errorsAtStart = useRef(errors)

  // Close once the download that this modal started has succeeded. A failure
  // keeps it open so the user can retry with the toast still in view. This
  // leans on apiState writing a fresh `errors[key]` object (via immer) on
  // every http_error, so a repeat failure still changes identity.
  useEffect(() => {
    if (isLoading && !wasLoading.current) {
      errorsAtStart.current = errors
    }
    if (wasLoading.current && !isLoading && errors === errorsAtStart.current) {
      onClose()
    }
    wasLoading.current = isLoading
  }, [isLoading, errors, onClose])

  return (
    <Modal isOpen={isOpen} padding="sm" showClose={true} onClose={onClose}>
      <Modal.Header title="Download TPN Report" />
      <Modal.Body
        actions={
          isLoading
            ? []
            : [
                {
                  label: 'Download',
                  onClick: () =>
                    dispatch(
                      reportingApiEvents.tpnReport_request({
                        dataType: 'text/csv',
                        dateParams: { start: selectedStartDate, end: selectedEndDate },
                      }),
                    ),
                },
              ]
        }
      >
        <DimmerLoader loading={isLoading} fadeStyle="flat" bgColor="white">
          <>
            <p>Select a date range and click the button below to download a CSV version of this report.</p>
            <DateRangePicker
              startDate={selectedStartDate}
              endDate={selectedEndDate}
              onChange={(date: $TSFixMe, isStartDate: boolean) => {
                isStartDate
                  ? setSelectedStartDate(format(new Date(date), dateFormat))
                  : setSelectedEndDate(format(new Date(date), dateFormat))
              }}
              inline={false}
            />
          </>
        </DimmerLoader>
      </Modal.Body>
    </Modal>
  )
}

export default DownloadTpnReportModal
